import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";

import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import "./styles/Blog.css";
import "../styles/global.css";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }}>
    {d}
  </svg>
);
const SearchIcon    = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />;
const PlusIcon      = (p) => <Icon {...p} d={<path d="M12 5v14M5 12h14" />} />;
const EditIcon      = (p) => <Icon {...p} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></>} />;
const TrashIcon     = (p) => <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />;
const CloseIcon     = (p) => <Icon {...p} d={<path d="M18 6 6 18M6 6l12 12" />} />;
const FileTextIcon  = (p) => <Icon {...p} d={<><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></>} />;
const ImageIcon     = (p) => <Icon {...p} d={<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>} />;
const TagIcon       = (p) => <Icon {...p} d={<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>} />;
const ChevronLeftIcon  = (p) => <Icon {...p} d={<path d="m15 18-6-6 6-6" />} />;
const ChevronRightIcon = (p) => <Icon {...p} d={<path d="m9 18 6-6-6-6" />} />;
const UploadIcon    = (p) => <Icon {...p} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>} />;
const UserIcon      = (p) => <Icon {...p} d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>} />;
const LinkIcon      = (p) => <Icon {...p} d={<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>} />;
const ChevronDownIcon = (p) => <Icon {...p} d={<path d="m6 9 6 6 6-6" />} />;
const CheckIcon     = (p) => <Icon {...p} d={<path d="M20 6 9 17l-5-5" />} />;

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Related Blogs Multi-Select ────────── */
const RelatedBlogsSelect = ({ blogs, selected, onChange, currentId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = blogs.filter(
    (b) =>
      b.id !== currentId &&
      b.name?.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const removeTag = (id) => onChange(selected.filter((s) => s !== id));

  const selectedBlogs = blogs.filter((b) => selected.includes(b.id));

  return (
    <div className="bl-ms-wrap" ref={ref}>
      {/* Selected tags */}
      {selectedBlogs.length > 0 && (
        <div className="bl-ms-tags">
          {selectedBlogs.map((b) => (
            <span key={b.id} className="bl-ms-tag">
              {b.name.length > 30 ? b.name.substring(0, 30) + "…" : b.name}
              <button type="button" onClick={() => removeTag(b.id)} className="bl-ms-tag-remove">
                <CloseIcon size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Trigger */}
      <button
        type="button"
        className={`bl-ms-trigger ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <LinkIcon size={14} />
        <span>{selected.length === 0 ? "Select related blogs…" : `${selected.length} blog${selected.length > 1 ? "s" : ""} selected`}</span>
        <ChevronDownIcon size={14} style={{ marginLeft: "auto", transition: "transform .2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="bl-ms-dropdown">
          <div className="bl-ms-search">
            <SearchIcon size={13} />
            <input
              type="text"
              placeholder="Search blogs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="bl-ms-search-clear">
                <CloseIcon size={12} />
              </button>
            )}
          </div>

          <div className="bl-ms-list">
            {filtered.length === 0 ? (
              <div className="bl-ms-empty">No blogs found</div>
            ) : (
              filtered.map((b) => {
                const isChecked = selected.includes(b.id);
                return (
                  <div
                    key={b.id}
                    className={`bl-ms-option ${isChecked ? "is-checked" : ""}`}
                    onClick={() => toggle(b.id)}
                  >
                    <span className={`bl-ms-checkbox ${isChecked ? "is-checked" : ""}`}>
                      {isChecked && <CheckIcon size={10} stroke={2.5} />}
                    </span>
                    <div className="bl-ms-option-info">
                      <span className="bl-ms-option-name">{b.name}</span>
                      {b.category && <span className="bl-ms-option-cat">{b.category}</span>}
                    </div>
                    {b.blogimg && (
                      <img
                        src={`${API_BASE_URL}public/blog/${b.blogimg}`}
                        alt={b.name}
                        className="bl-ms-option-thumb"
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {selected.length > 0 && (
            <div className="bl-ms-footer">
              <button type="button" className="bl-ms-clear-all" onClick={() => { onChange([]); setOpen(false); }}>
                Clear all ({selected.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="bl-pagination">
      <div className="bl-pagination__info">
        <span>Showing <b>{totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–<b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b></span>
        <select className="bl-pagination__perpage" value={perPage} onChange={(e) => onPerPageChange(Number(e.target.value))}>
          {[10, 25, 50, 100].map((n) => (<option key={n} value={n}>{n} / page</option>))}
        </select>
      </div>
      <div className="bl-pagination__pages">
        <button className="bl-pagination__btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} aria-label="Previous page"><ChevronLeftIcon size={16} /></button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="bl-pagination__ellipsis">…</span>
          ) : (
            <button key={p} className={`bl-pagination__btn ${p === currentPage ? "is-active" : ""}`} onClick={() => onPageChange(p)} aria-current={p === currentPage ? "page" : undefined}>{p}</button>
          )
        )}
        <button className="bl-pagination__btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(currentPage + 1)} aria-label="Next page"><ChevronRightIcon size={16} /></button>
      </div>
    </div>
  );
};

/* ────────── Main Component ────────── */
const Blog = () => {
  const [loading, setLoading]               = useState(false);
  const [blogs, setBlogs]                   = useState([]);
  const [filteredBlogs, setFilteredBlogs]   = useState([]);
  const [filterText, setFilterText]         = useState("");
  const [categories, setCategories]         = useState([]);

  // Form fields
  const [name, setName]                     = useState("");
  const [shortDesc, setShortDesc]           = useState("");
  const [content, setContent]               = useState("");
  const [category, setCategory]             = useState("");
  const [status, setStatus]                 = useState("Active");
  const [authorName, setAuthorName]         = useState("");
  const [relatedBlogs, setRelatedBlogs]     = useState([]); // array of IDs

  const [image, setImage]                   = useState(null);
  const [preview, setPreview]               = useState(null);

  const [isEditMode, setIsEditMode]         = useState(false);
  const [editId, setEditId]                 = useState(null);
  const [showModal, setShowModal]           = useState(false);
  const [messagealert, setMessageAlert]     = useState("");
  const [messageType, setMessageType]       = useState("");
  const [currentPage, setCurrentPage]       = useState(1);
  const [perPage, setPerPage]               = useState(10);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", confirmLabel: "Confirm", confirmVariant: "danger", onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, onConfirm, confirmLabel = "Confirm", confirmVariant = "danger" }) => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmLabel, confirmVariant });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  const token = localStorage.getItem("authToken");

  /* ── Image preview ── */
  useEffect(() => {
    if (!image) { setPreview(null); return; }
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  /* ── Fetch blogs ── */
  const fetchBlogs = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/blogs`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) { setBlogs(data.data); setFilteredBlogs(data.data); }
      })
      .finally(() => setLoading(false));
  };

  /* ── Fetch categories ── */
  const fetchCategories = () => {
    fetch(`${API_BASE_URL}api/blog-categories`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => { if (data.status) setCategories(data.data); });
  };

  useEffect(() => { fetchBlogs(); fetchCategories(); }, []);

  /* ── Search filter ── */
  useEffect(() => {
    const result = blogs.filter((b) => b.name?.toLowerCase().includes(filterText.toLowerCase()));
    setFilteredBlogs(result);
    setCurrentPage(1);
  }, [filterText, blogs]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredBlogs.length / perPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filteredBlogs.length, perPage, currentPage]);

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg); setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
  };

  /* ── Reset form ── */
  const resetForm = () => {
    setName(""); setShortDesc(""); setContent(""); setCategory("");
    setStatus("Active"); setAuthorName(""); setRelatedBlogs([]);
    setImage(null); setPreview(null); setIsEditMode(false); setEditId(null);
  };

  /* ── Save / Update ── */
  const handleSave = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("short_desc", shortDesc);
    formData.append("content", content);
    formData.append("category", category);
    formData.append("status", status);
    formData.append("author_name", authorName);
    formData.append("related_blogs", JSON.stringify(relatedBlogs));
    if (image) formData.append("blogimg", image);

    const url = isEditMode
      ? `${API_BASE_URL}api/blogs/${editId}`
      : `${API_BASE_URL}api/blogs`;

    fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((res) => res.json())
      .then(() => {
        fetchBlogs();
        flashMessage(isEditMode ? "Blog updated successfully!" : "Blog added successfully!", "success");
        resetForm();
        setShowModal(false);
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  /* ── Edit ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditId(row.id);
    setName(row.name);
    setShortDesc(row.short_desc || "");
    setContent(row.content || "");
    setCategory(row.category || "");
    setStatus(row.status || "Active");
    setAuthorName(row.author_name || "");

    // Parse related_blogs — stored as JSON string in DB
    try {
      const parsed = row.related_blogs ? JSON.parse(row.related_blogs) : [];
      setRelatedBlogs(Array.isArray(parsed) ? parsed : []);
    } catch {
      setRelatedBlogs([]);
    }

    setPreview(row.blogimg ? `${API_BASE_URL}public/blog/${row.blogimg}` : null);
    setImage(null);
    setShowModal(true);
  };

  /* ── Delete ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Blog Post",
      message: "Are you sure you want to delete this blog post? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/blogs/destroy/${id}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(() => { fetchBlogs(); flashMessage("Blog deleted successfully!", "success"); })
          .catch(() => flashMessage("Error deleting blog.", "danger"));
      },
    });
  };

  /* ── Open Add modal ── */
  const openAddModal = () => { resetForm(); setShowModal(true); };

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(filteredBlogs.length / perPage));
  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredBlogs.slice(start, start + perPage);
  }, [filteredBlogs, currentPage, perPage]);

  /* ── Table Columns ── */
  const columns = useMemo(() => [
    {
      name: "Image",
      cell: (row) => (
        <img
          src={row.blogimg ? `${API_BASE_URL}public/blog/${row.blogimg}` : `${API_BASE_URL}public/noimage.jpg`}
          alt={row.name}
          className="bl-row-thumb"
        />
      ),
      width: "80px",
    },
    {
      name: "Title",
      selector: (row) => row.name,
      cell: (row) => (
        <div className="bl-cell-title-wrap">
          <span className="bl-cell-name">{row.name}</span>
          {row.author_name && (
            <span className="bl-cell-author">
              <UserIcon size={11} /> {row.author_name}
            </span>
          )}
        </div>
      ),
      sortable: true,
      grow: 3,
    },
    {
      name: "Category",
      selector: (row) => row.category,
      cell: (row) => (
        <span className="bl-cat-pill">
          <TagIcon size={11} />
          {row.category}
        </span>
      ),
      sortable: true,
      grow: 1,
    },
    {
      name: "Related",
      cell: (row) => {
        let count = 0;
        try { count = row.related_blogs ? JSON.parse(row.related_blogs).length : 0; } catch {}
        return count > 0 ? (
          <span className="bl-related-badge"><LinkIcon size={11} /> {count}</span>
        ) : <span className="bl-related-none">—</span>;
      },
      width: "90px",
      center: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <span className={`bl-status-pill ${row.status === "Active" ? "is-active" : "is-inactive"}`}>
          <span className="dot" />{row.status}
        </span>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="bl-row-actions">
          <button className="bl-ico-btn edit" onClick={() => handleEdit(row)} title="Edit Blog"><EditIcon size={15} /></button>
          <button className="bl-ico-btn del" onClick={() => handleDelete(row.id)} title="Delete Blog"><TrashIcon size={15} /></button>
        </div>
      ),
      width: "110px",
      right: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  /* ── DataTable Custom Styles ── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: { style: { background: "#f0fbf6", borderTopLeftRadius: "12px", borderTopRightRadius: "12px", borderBottom: "1px solid #d6efe2", minHeight: "52px" } },
    headCells: { style: { fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5b6b66", paddingLeft: "16px", paddingRight: "16px" } },
    rows: { style: { minHeight: "72px", fontSize: "14px", color: "#1f2a37", borderBottom: "1px solid #eef4f1 !important", transition: "background 0.18s ease" }, highlightOnHoverStyle: { background: "#f6fcf9", borderBottomColor: "#e3f0e9", outline: "none" }, stripedStyle: { background: "#fafefb" } },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  };

  /* ────────── Render ────────── */
  return (
    <>
      <style>{`
        /* ── Related Blogs Multi-Select ── */
        .bl-ms-wrap { position: relative; }
        .bl-ms-tags {
          display: flex; flex-wrap: wrap; gap: 6px;
          margin-bottom: 8px;
        }
        .bl-ms-tag {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px 4px 12px;
          background: #e8f5f2; color: #0f5f4e;
          border: 1px solid #b7e0d4; border-radius: 999px;
          font-size: 12px; font-weight: 600;
          max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .bl-ms-tag-remove {
          background: rgba(15,95,78,.12); border: none; border-radius: 50%;
          width: 16px; height: 16px; display: flex; align-items: center; justify-content: center;
          cursor: pointer; padding: 0; color: #0f5f4e; flex-shrink: 0;
          transition: background .14s;
        }
        .bl-ms-tag-remove:hover { background: rgba(15,95,78,.25); }
        .bl-ms-trigger {
          display: flex; align-items: center; gap: 8px;
          width: 100%; padding: 10px 14px;
          background: #f8faf9; border: 1.5px solid #d6efe2;
          border-radius: 10px; cursor: pointer;
          font-size: 13.5px; color: #4a5a55;
          font-family: inherit; transition: all .15s;
        }
        .bl-ms-trigger svg { fill: none !important; color: #10b981; flex-shrink: 0; }
        .bl-ms-trigger:hover, .bl-ms-trigger.is-open {
          border-color: #10b981; background: #fff;
          box-shadow: 0 0 0 3px rgba(16,185,129,.1);
        }
        .bl-ms-dropdown {
          position: absolute; top: calc(100% + 6px); left: 0; right: 0;
          background: #fff; border: 1.5px solid #d6efe2; border-radius: 14px;
          box-shadow: 0 12px 40px rgba(0,0,0,.14); z-index: 500;
          overflow: hidden;
          animation: blDropIn .18s ease;
        }
        @keyframes blDropIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        .bl-ms-search {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 14px; border-bottom: 1px solid #eef4f1;
          background: #f8faf9;
        }
        .bl-ms-search svg { fill: none !important; color: #10b981; flex-shrink: 0; }
        .bl-ms-search input {
          flex: 1; border: none; background: transparent; outline: none;
          font-size: 13px; color: #1f2a37; font-family: inherit;
        }
        .bl-ms-search input::placeholder { color: #9aada6; }
        .bl-ms-search-clear {
          background: #e5eeeb; border: none; border-radius: 50%;
          width: 18px; height: 18px; display: flex; align-items: center; justify-content: center;
          cursor: pointer; padding: 0; color: #5b6b66;
        }
        .bl-ms-list { max-height: 220px; overflow-y: auto; padding: 6px; }
        .bl-ms-list::-webkit-scrollbar { width: 4px; }
        .bl-ms-list::-webkit-scrollbar-thumb { background: #d6efe2; border-radius: 4px; }
        .bl-ms-option {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 10px; border-radius: 9px; cursor: pointer;
          transition: background .13s;
        }
        .bl-ms-option:hover { background: #f0fbf6; }
        .bl-ms-option.is-checked { background: #e8f5f2; }
        .bl-ms-checkbox {
          width: 17px; height: 17px; border-radius: 5px;
          border: 1.5px solid #c0d5cc; background: #fff;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: all .14s;
        }
        .bl-ms-checkbox.is-checked { background: #10b981; border-color: #10b981; }
        .bl-ms-checkbox svg { fill: none !important; color: #fff; }
        .bl-ms-option-info { flex: 1; min-width: 0; }
        .bl-ms-option-name {
          font-size: 13px; font-weight: 600; color: #1f2a37;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .bl-ms-option-cat {
          display: block; font-size: 11px; color: #7a8f88; margin-top: 1px;
        }
        .bl-ms-option-thumb {
          width: 32px; height: 32px; border-radius: 6px; object-fit: cover;
          border: 1px solid #d6efe2; flex-shrink: 0;
        }
        .bl-ms-empty { padding: 16px; text-align: center; font-size: 13px; color: #9aada6; }
        .bl-ms-footer {
          padding: 8px 14px; border-top: 1px solid #eef4f1;
          background: #f8faf9; display: flex; justify-content: flex-end;
        }
        .bl-ms-clear-all {
          background: none; border: none; color: #e05252; font-size: 12.5px;
          font-weight: 600; cursor: pointer; font-family: inherit;
        }
        .bl-ms-clear-all:hover { text-decoration: underline; }

        /* ── Table cell extras ── */
        .bl-cell-title-wrap { display: flex; flex-direction: column; gap: 3px; }
        .bl-cell-author {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11.5px; color: #7a8f88; font-weight: 500;
        }
        .bl-cell-author svg { fill: none !important; }
        .bl-related-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 9px; background: #e8f5f2; color: #0f5f4e;
          border: 1px solid #b7e0d4; border-radius: 999px;
          font-size: 11.5px; font-weight: 700;
        }
        .bl-related-badge svg { fill: none !important; }
        .bl-related-none { color: #c0ccc8; font-size: 13px; }

        /* ── Form section divider ── */
        .bl-section-label {
          font-size: 11px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.07em; color: #10b981;
          display: flex; align-items: center; gap: 8px;
          margin: 20px 0 12px;
        }
        .bl-section-label::after {
          content: ""; flex: 1; height: 1px;
          background: linear-gradient(90deg, #d6efe2, transparent);
        }

        /* ── Confirm Dialog ── */
        .cdlg-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(15,31,26,.45); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px; animation: cdlgIn .18s ease;
        }
        @keyframes cdlgIn { from { opacity:0; } to { opacity:1; } }
        .cdlg-box {
          background: #fff; border-radius: 20px;
          box-shadow: 0 24px 60px rgba(15,31,26,.2);
          padding: 32px 28px 24px; width: 100%; max-width: 400px; text-align: center;
          animation: cdlgUp .22s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes cdlgUp { from { opacity:0; transform:translateY(18px) scale(.97); } to { opacity:1; transform:translateY(0) scale(1); } }
        .cdlg-icon-wrap { width:60px; height:60px; border-radius:18px; display:flex; align-items:center; justify-content:center; margin:0 auto 18px; }
        .cdlg-icon-danger { background:#fff1f0; color:#e0413c; }
        .cdlg-icon-warning { background:#fffbeb; color:#d97706; }
        .cdlg-title { font-size:18px; font-weight:800; color:#0f1f1a; margin:0 0 8px; }
        .cdlg-message { font-size:14px; color:#6b7a74; line-height:1.6; margin:0 0 24px; }
        .cdlg-actions { display:flex; gap:10px; justify-content:center; }
        .cdlg-btn { flex:1; padding:11px 20px; border-radius:10px; border:none; font-size:14px; font-weight:600; cursor:pointer; transition:all .15s; }
        .cdlg-btn-cancel { background:#f1f5f9; color:#475569; border:1.5px solid #e2e8f0; }
        .cdlg-btn-cancel:hover { background:#e2e8f0; }
        .cdlg-btn-confirm { color:#fff; }
        .cdlg-btn-danger { background:linear-gradient(135deg,#ef4444,#f43f5e); box-shadow:0 4px 14px rgba(239,68,68,.28); }
        .cdlg-btn-danger:hover { box-shadow:0 6px 20px rgba(239,68,68,.4); transform:translateY(-1px); }
      `}</style>

      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="bl-overlay"><div className="bl-spinner" /></div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="bl-shell">

              {/* ── Page Header ── */}
              <div className="bl-head">
                <div>
                  <h2>Blog Posts</h2>
                  <p>Create and manage all your blog content in one place.</p>
                </div>
                <button type="button" className="bl-add-btn" onClick={openAddModal}>
                  <PlusIcon size={18} /> Add Blog
                </button>
              </div>

              {/* ── Main Card ── */}
              <div className="bl-card">
                <div className="bl-card-top">
                  <div className="bl-search-box">
                    <SearchIcon size={17} />
                    <input
                      type="text"
                      placeholder="Search by title…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <div className="bl-total-badge">
                    <FileTextIcon size={15} />
                    Total:&nbsp;<b>{filteredBlogs.length}</b>
                  </div>
                </div>

                {messagealert && (
                  <div className={`bl-alert ${messageType}`} role="alert">
                    <span>{messagealert}</span>
                    <button type="button" onClick={() => setMessageAlert("")} aria-label="Close"><CloseIcon size={16} /></button>
                  </div>
                )}

                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  striped
                  noDataComponent={
                    <div className="bl-empty">
                      <FileTextIcon size={40} />
                      <p>No blog posts found</p>
                      <span>Try adjusting your search or write a new post</span>
                    </div>
                  }
                />

                {filteredBlogs.length > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    perPage={perPage}
                    totalRows={filteredBlogs.length}
                    onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ────────── Add / Edit Modal ────────── */}
          {showModal && (
            <div
              className="bl-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
              aria-modal="true"
              role="dialog"
              aria-labelledby="blModalTitle"
            >
              <div className="bl-modal">
                {/* Modal Header */}
                <div className="bl-modal__header">
                  <div className="bl-modal__header-left">
                    <div className="bl-modal__icon"><FileTextIcon size={20} /></div>
                    <div>
                      <p className="bl-modal__title" id="blModalTitle">
                        {isEditMode ? "Edit Blog Post" : "Add New Blog Post"}
                      </p>
                      <p className="bl-modal__subtitle">
                        {isEditMode ? "Update the content and details of this post" : "Fill in the details to publish a new blog post"}
                      </p>
                    </div>
                  </div>
                  <button type="button" className="bl-modal__close" onClick={() => setShowModal(false)} aria-label="Close modal">
                    <CloseIcon size={16} />
                  </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSave}>
                  <div className="bl-modal__body">

                    {/* ── CONTENT SECTION ── */}
                    <p className="bl-section-label">Content</p>

                    {/* Title */}
                    <div className="bl-form-group">
                      <label className="bl-form-label" htmlFor="blTitle">Title <span>*</span></label>
                      <input
                        type="text" id="blTitle" className="bl-form-control"
                        placeholder="Enter blog title"
                        value={name} onChange={(e) => setName(e.target.value)}
                        required autoFocus
                      />
                    </div>

                    {/* Short Description */}
                    <div className="bl-form-group">
                      <label className="bl-form-label" htmlFor="blShortDesc">Short Description</label>
                      <textarea
                        id="blShortDesc" className="bl-form-control"
                        placeholder="Brief summary of the post…"
                        value={shortDesc} onChange={(e) => setShortDesc(e.target.value)}
                      />
                    </div>

                    {/* Content – SunEditor */}
                    <div className="bl-form-group">
                      <label className="bl-form-label">Content</label>
                      <div className="bl-editor-wrap">
                        <SunEditor
                          key={isEditMode ? `edit-${editId}` : "add"}
                          setContents={content}
                          onChange={(value) => setContent(value)}
                          height="220px"
                          setOptions={{
                            buttonList: [
                              ["bold", "underline", "italic", "strike"],
                              ["fontColor", "hiliteColor"],
                              ["align", "list", "lineHeight"],
                              ["link", "image"],
                              ["undo", "redo"],
                              ["fullScreen"],
                            ],
                          }}
                        />
                      </div>
                    </div>

                    {/* ── META SECTION ── */}
                    <p className="bl-section-label">Details</p>

                    {/* Author Name */}
                    <div className="bl-form-group">
                      <label className="bl-form-label" htmlFor="blAuthor">
                        <UserIcon size={13} style={{ display: "inline", verticalAlign: "middle", marginRight: 5 }} />
                        Author Name
                      </label>
                      <input
                        type="text" id="blAuthor" className="bl-form-control"
                        placeholder="e.g. Dr. Saravanan"
                        value={authorName} onChange={(e) => setAuthorName(e.target.value)}
                      />
                    </div>

                    {/* Category + Status row */}
                    <div className="bl-form-row">
                      <div>
                        <label className="bl-form-label" htmlFor="blCategory">Category <span>*</span></label>
                        <select id="blCategory" className="bl-form-select" value={category} onChange={(e) => setCategory(e.target.value)} required>
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.slug}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="bl-form-label" htmlFor="blStatus">Status</label>
                        <select id="blStatus" className="bl-form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>
                    </div>

                    {/* ── RELATED SECTION ── */}
                    <p className="bl-section-label">Related Blogs</p>

                    <div className="bl-form-group">
                      <label className="bl-form-label">
                        <LinkIcon size={13} style={{ display: "inline", verticalAlign: "middle", marginRight: 5 }} />
                        Related Blog Posts
                        {relatedBlogs.length > 0 && (
                          <span style={{ marginLeft: 8, fontSize: 11, background: "#10b981", color: "#fff", padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>
                            {relatedBlogs.length} selected
                          </span>
                        )}
                      </label>
                      <RelatedBlogsSelect
                        blogs={blogs}
                        selected={relatedBlogs}
                        onChange={setRelatedBlogs}
                        currentId={editId}
                      />
                      <p style={{ fontSize: 11.5, color: "#9aada6", marginTop: 6 }}>
                        These blogs will appear as "You might also like" suggestions.
                      </p>
                    </div>

                    {/* ── MEDIA SECTION ── */}
                    <p className="bl-section-label">Media</p>

                    {/* Featured Image */}
                    <div className="bl-form-group">
                      <label className="bl-form-label">
                        <ImageIcon size={13} style={{ display: "inline", verticalAlign: "middle", marginRight: 5 }} />
                        Featured Image
                      </label>
                      <div className="bl-file-upload">
                        <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} />
                        <div className="bl-file-upload__inner">
                          <UploadIcon size={22} />
                          <p className="bl-file-upload__text"><b>Click to upload</b> or drag & drop</p>
                          <p className="bl-file-upload__text" style={{ fontSize: 12 }}>PNG, JPG, WEBP up to 5MB</p>
                        </div>
                      </div>
                      {preview && <img src={preview} alt="Preview" className="bl-preview-thumb" />}
                    </div>

                  </div>

                  {/* Modal Footer */}
                  <div className="bl-modal__footer">
                    <button type="button" className="bl-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="bl-btn-submit">
                      {isEditMode ? <><EditIcon size={15} /> Update Post</> : <><PlusIcon size={15} /> Publish Post</>}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Blog;